<template>
  <div class="aml-detail-root">

    <!-- ══ Loading ══ -->
    <div v-if="loading" class="aml-detail-wrap">
      <div class="aml-detail-header">
        <v-btn icon disabled class="mr-2"><v-icon>mdi-arrow-left</v-icon></v-btn>
        <div class="flex-grow-1">
          <v-skeleton-loader type="heading" width="220" class="mb-1" />
          <v-skeleton-loader type="text" width="300" />
        </div>
      </div>
      <v-row>
        <v-col cols="12" md="7"><v-skeleton-loader type="card" height="260" class="mb-4" /></v-col>
        <v-col cols="12" md="5"><v-skeleton-loader type="card" height="220" /></v-col>
      </v-row>
    </div>

    <!-- ══ Error ══ -->
    <div v-else-if="!amlCase" class="aml-detail-wrap text-center py-12">
      <v-icon size="52" color="grey lighten-1">mdi-file-alert-outline</v-icon>
      <div class="subtitle-1 grey--text mt-3">Case could not be loaded</div>
      <v-btn depressed small color="primary" class="mt-4" @click="$router.push({ name: 'watchlist-dashboard' })">
        <v-icon small left>mdi-arrow-left</v-icon>Back to dashboard
      </v-btn>
    </div>

    <!-- ══ Content ══ -->
    <div v-else class="aml-detail-wrap">

      <!-- ── New search activity banner ── -->
      <v-alert
        v-if="amlCase.hasNewSearchActivity"
        dense outlined type="warning"
        class="mb-4"
        style="border-radius:10px"
      >
        <div class="d-flex align-center justify-space-between">
          <span class="body-2">A new watchlist search was performed while this case is active. Review the latest search result on the flag.</span>
          <v-btn x-small depressed @click="acknowledgeActivity">Dismiss</v-btn>
        </div>
      </v-alert>

      <!-- ── Header ── -->
      <div class="aml-detail-header">
        <button class="aml-back-btn" @click="$router.push({ name: 'watchlist-dashboard' })">
          <v-icon small>mdi-arrow-left</v-icon>
        </button>
        <div class="flex-grow-1 ml-3">
          <div class="aml-case-ref">{{ amlCase.caseReference }}</div>
          <div class="aml-case-sub">
            Opened by {{ assigneeName(amlCase.startedBy) }} · {{ formatDate(amlCase.dateCreated, 'YYYY-MM-DD') }}
            <span v-if="amlCase.reopenCount > 0"> · Reopened {{ amlCase.reopenCount }}×</span>
          </div>
        </div>
        <div class="d-flex align-center" style="gap:6px;flex-wrap:wrap">
          <span class="aml-status-pill" :style="caseStatusStyle(amlCase.status)">{{ formatStatus(amlCase.status) }}</span>
          <span class="aml-status-pill" :style="priorityPillStyle(amlCase.priority)">{{ amlCase.priority }}</span>
          <template v-if="!isClosed">
            <div class="aml-header-divider" />
            <button class="aml-hdr-btn" @click="commentDialog = true">
              <v-icon x-small left>mdi-comment-plus-outline</v-icon>Add note
            </button>
            <button class="aml-hdr-btn aml-hdr-btn--danger" @click="closeDialog = true">
              <v-icon x-small left>mdi-lock-outline</v-icon>Close case
            </button>
          </template>
        </div>
      </div>

      <!-- ── Tabs ── -->
      <div class="aml-tabs-bar">
        <button v-for="(t, i) in tabDefs" :key="i" class="aml-tab" :class="{ 'aml-tab--active': tab === i }" @click="tab = i">
          {{ t.label }}
          <span v-if="t.badge !== undefined" class="aml-tab-badge" :class="t.badgeDone ? 'aml-tab-badge--done' : ''">{{ t.badge }}</span>
        </button>
      </div>

      <!-- ══ TAB 0 Overview ══ -->
      <div v-show="tab === 0" class="aml-tab-content">
        <v-row dense>

          <!-- Watchlist flag summary -->
          <v-col cols="12" md="7">
            <!-- All linked watchlist flags -->
            <div v-if="allLinkedFlags.length" class="aml-panel mb-4">
              <div class="aml-panel-head">
                <div class="aml-panel-icon" style="background:#FCEBEB">
                  <v-icon small color="#A32D2D">mdi-alert-circle-outline</v-icon>
                </div>
                <span class="aml-panel-title">Watchlist flags</span>
                <span class="aml-tab-badge ml-2">{{ allLinkedFlags.length }}</span>
                <v-spacer />
                <span class="aml-muted" style="font-size:11px">{{ allLinkedFlags.length }} provider{{ allLinkedFlags.length !== 1 ? 's' : '' }} under investigation</span>
              </div>

              <!-- One row per linked flag -->
              <div
                v-for="(link, li) in allLinkedFlags"
                :key="link.flagId || link.id"
                class="aml-flag-link-row"
                :class="li < allLinkedFlags.length - 1 ? 'aml-flag-link-row--bordered' : ''"
              >
                <div class="d-flex align-center justify-space-between mb-2">
                  <div class="d-flex align-center" style="gap:6px">
                    <span class="aml-type-badge" style="font-size:11px;font-weight:700">{{ providerShort(link.provider) }}</span>
                    <span v-if="link.isPrimary" class="aml-type-badge" style="background:#E6F1FB;color:#185FA5;font-size:9px">Primary</span>
                    <span class="aml-pill" :style="flagStatusStyle(link.flagStatus)">{{ flagStatusLabel(link.flagStatus) }}</span>
                  </div>
                  <v-btn x-small text color="primary" @click="goToFlag(link.flagId || link.id)">
                    <v-icon x-small left>mdi-eye</v-icon>View flag
                  </v-btn>
                </div>
                <div class="aml-grid-2">
                  <div class="aml-field">
                    <div class="aml-label">List</div>
                    <div class="aml-value">{{ link.listName || '—' }}</div>
                  </div>
                  <div v-if="link.lastReviewedStatus" class="aml-field">
                    <div class="aml-label">Prior outcome</div>
                    <div class="aml-value">
                      <span class="aml-pill" :style="flagStatusStyle(link.lastReviewedStatus)">{{ flagStatusLabel(link.lastReviewedStatus) }}</span>
                    </div>
                  </div>
                </div>

                <!-- Latest hit from this flags most recent search log -->
                <template v-if="link.latestLog && link.latestLog.matchDetails && link.latestLog.matchDetails.length">
                  <div class="aml-divider mt-2 mb-2" />
                  <div class="aml-section-label mb-2">Latest matches ({{ link.latestLog.matchDetails.length }})</div>
                  <div
                    v-for="(hit, hi) in link.latestLog.matchDetails.slice(0, 2)"
                    :key="hi"
                    class="aml-hit-card mb-1"
                    style="cursor:pointer"
                    @click="$set(expandedCaseHits, link.flagId + '_' + hi, !expandedCaseHits[link.flagId + '_' + hi])"
                  >
                    <div class="d-flex align-center justify-space-between">
                      <div class="d-flex align-center" style="gap:6px;flex:1;min-width:0">
                        <v-icon x-small :color="expandedCaseHits[link.flagId + '_' + hi] ? '#185FA5' : '#bbb'">
                          {{ expandedCaseHits[link.flagId + '_' + hi] ? 'mdi-chevron-down' : 'mdi-chevron-right' }}
                        </v-icon>
                        <span style="font-size:11px;font-weight:600;overflow:hidden;text-overflow:ellipsis;white-space:nowrap">{{ hit.name }}</span>
                        <span class="aml-type-badge" style="font-size:9px">{{ hit.source_code }}</span>
                      </div>
                      <span class="aml-pill" style="background:#FCEBEB;color:#A32D2D;font-size:9px;flex-shrink:0">{{ hit.score }}</span>
                    </div>
                    <div v-if="expandedCaseHits[link.flagId + '_' + hi]" class="mt-1 pt-1" style="border-top:0.5px solid #eee">
                      <div v-if="hit.aliases" class="aml-muted" style="font-size:10px"><strong>Aliases:</strong> {{ hit.aliases }}</div>
                      <div v-if="hit.address" class="aml-muted" style="font-size:10px"><strong>Address:</strong> {{ hit.address }}</div>
                      <div v-if="hit.last_updated" class="aml-muted" style="font-size:10px"><strong>List date:</strong> {{ describeListDate(hit.last_updated, hit.date_method) }}</div>
                      <a v-if="isSafeUrl(hit.source_url)" :href="hit.source_url" target="_blank" rel="noopener noreferrer" class="aml-source-link" @click.stop>
                        <v-icon x-small color="#185FA5">mdi-open-in-new</v-icon>View at source
                      </a>
                    </div>
                  </div>
                  <v-btn x-small text color="primary" class="mt-1" @click="tab = 2">
                    All search history <v-icon x-small right>mdi-arrow-right</v-icon>
                  </v-btn>
                </template>
              </div>
            </div>

            <!-- Case details -->
            <div class="aml-panel mb-4">
              <div class="aml-panel-head">
                <div class="aml-panel-icon" style="background:#E6F1FB">
                  <v-icon small color="#185FA5">mdi-folder-outline</v-icon>
                </div>
                <span class="aml-panel-title">Case details</span>
              </div>
              <div class="pa-3">
                <div class="aml-grid-2 mb-3">
                  <div class="aml-field">
                    <div class="aml-label">Status</div>
                    <div class="aml-value"><span class="aml-pill" :style="caseStatusStyle(amlCase.status)">{{ formatStatus(amlCase.status) }}</span></div>
                  </div>
                  <div class="aml-field">
                    <div class="aml-label">Priority</div>
                    <div class="aml-value"><span class="aml-pill" :style="priorityPillStyle(amlCase.priority)">{{ amlCase.priority }}</span></div>
                  </div>
                  <div class="aml-field">
                    <div class="aml-label">Current stage</div>
                    <div class="aml-value">{{ amlCase.reportStage || '—' }}</div>
                  </div>
                  <div class="aml-field">
                    <div class="aml-label">Assigned to</div>
                    <div class="aml-value">{{ amlCase.assignedTo ? assigneeName(amlCase.assignedTo) : 'Unassigned' }}</div>
                  </div>
                  <div v-if="amlCase.dateClosed" class="aml-field">
                    <div class="aml-label">Date closed</div>
                    <div class="aml-value">{{ formatDate(amlCase.dateClosed, 'YYYY-MM-DD') }}</div>
                  </div>
                  <div v-if="amlCase.closeReason" class="aml-field" style="grid-column:1/-1">
                    <div class="aml-label">Close reason</div>
                    <div class="aml-value">{{ amlCase.closeReason }}</div>
                  </div>
                </div>
                <div v-if="amlCase.summary" class="aml-field">
                  <div class="aml-label">Summary</div>
                  <div class="aml-value" style="white-space:pre-wrap;margin-top:4px">{{ amlCase.summary }}</div>
                </div>
              </div>
            </div>
          </v-col>

          <!-- Sidebar: person info + assign -->
          <v-col cols="12" md="5">

            <!-- Person under investigation -->
            <div v-if="subjectPerson" class="aml-panel mb-4">
              <div class="aml-panel-head">
                <div class="aml-panel-icon" style="background:#EEEDFE">
                  <v-icon small color="#534AB7">mdi-account-search-outline</v-icon>
                </div>
                <span class="aml-panel-title">Subject</span>
              </div>
              <div class="pa-3">
                <!-- Name + avatar -->
                <div class="d-flex align-center mb-3" style="gap:10px">
                  <div class="aml-person-avatar" style="width:40px;height:40px;font-size:12px;flex-shrink:0">
                    {{ avatarInitials(`${subjectPerson.firstName ?? ''} ${subjectPerson.lastName ?? ''}`) }}
                  </div>
                  <div>
                    <div style="font-size:14px;font-weight:600">
                      {{ subjectPerson.firstName }} {{ subjectPerson.lastName }}
                    </div>
                    <div v-if="subjectPerson.memberCategory" class="aml-muted" style="font-size:11px">
                      {{ subjectPerson.memberCategory }}
                    </div>
                  </div>
                </div>

                <div class="aml-grid-2">
                  <div v-if="subjectPerson.dateOfBirth" class="aml-field">
                    <div class="aml-label">Date of birth</div>
                    <div class="aml-value">{{ formatDate(subjectPerson.dateOfBirth, 'YYYY-MM-DD') }}</div>
                  </div>
                  <div v-if="subjectPerson.gender" class="aml-field">
                    <div class="aml-label">Gender</div>
                    <div class="aml-value">{{ subjectPerson.gender }}</div>
                  </div>
                  <div v-if="subjectPerson.trn" class="aml-field">
                    <div class="aml-label">TRN</div>
                    <div class="aml-value font-weight-medium">{{ subjectPerson.trn }}</div>
                  </div>
                  <div v-if="subjectPerson.maritalStatus" class="aml-field">
                    <div class="aml-label">Marital status</div>
                    <div class="aml-value">{{ subjectPerson.maritalStatus }}</div>
                  </div>
                </div>

                <!-- Contact -->
                <template v-if="subjectPerson.emailAddress || primaryContact">
                  <div class="aml-divider my-2" />
                  <div v-if="subjectPerson.emailAddress" class="aml-field mb-1">
                    <div class="aml-label">Email</div>
                    <div class="aml-value">{{ subjectPerson.emailAddress }}</div>
                  </div>
                  <div v-if="primaryContact" class="aml-field mb-1">
                    <div class="aml-label">{{ primaryContact.label || 'Phone' }}</div>
                    <div class="aml-value">{{ primaryContact.number || primaryContact.value }}</div>
                  </div>
                </template>

                <!-- Address -->
                <template v-if="subjectAddress">
                  <div class="aml-divider my-2" />
                  <div class="aml-field">
                    <div class="aml-label">Address</div>
                    <div class="aml-value" style="font-size:11px;line-height:1.5">{{ subjectAddress }}</div>
                  </div>
                </template>

                <!-- Risk -->
                <template v-if="subjectPerson.riskScore || subjectPerson.riskRating">
                  <div class="aml-divider my-2" />
                  <div class="aml-grid-2">
                    <div v-if="subjectPerson.riskRating" class="aml-field">
                      <div class="aml-label">Risk rating</div>
                      <div class="aml-value">{{ subjectPerson.riskRating }}</div>
                    </div>
                    <div v-if="subjectPerson.riskScore" class="aml-field">
                      <div class="aml-label">Risk score</div>
                      <div class="aml-value">{{ subjectPerson.riskScore }}</div>
                    </div>
                  </div>
                </template>

                <!-- ID documents -->
                <template v-if="subjectPerson.identifications && subjectPerson.identifications.length">
                  <div class="aml-divider my-2" />
                  <div class="aml-section-label mb-2">Identification</div>
                  <div
                    v-for="(doc, di) in subjectPerson.identifications.slice(0, 3)"
                    :key="di"
                    class="aml-field mb-1"
                  >
                    <div class="aml-label">{{ doc.idType || doc.type }}</div>
                    <div class="aml-value">{{ doc.idNumber || doc.number }}
                      <span v-if="doc.expiryDate || doc.idExpiryDate" class="aml-muted" style="font-size:10px">
                        · Exp: {{ doc.expiryDate || doc.idExpiryDate }}
                      </span>
                    </div>
                  </div>
                </template>
              </div>
            </div>

            <!-- Assign panel -->
            <div class="aml-panel mb-4">
              <div class="aml-panel-head">
                <div class="aml-panel-icon" style="background:#EAF3DE">
                  <v-icon small color="#3B6D11">mdi-account-outline</v-icon>
                </div>
                <span class="aml-panel-title">Assignment</span>
                <v-spacer />
                <template v-if="!isClosed">
                  <v-btn x-small text color="primary" @click="openAssignDialog">Reassign</v-btn>
                  <v-btn v-if="amlCase.assignedToUserId !== (loggedInUser && loggedInUser.id)" x-small text color="primary" @click="assignToSelf">Assign to me</v-btn>
                </template>
              </div>
              <div class="pa-3">
                <div v-if="amlCase.assignedTo" class="d-flex align-center" style="gap:10px">
                  <div class="aml-person-avatar" style="width:32px;height:32px;font-size:10px">{{ avatarInitials(assigneeName(amlCase.assignedTo)) }}</div>
                  <div>
                    <div style="font-size:13px;font-weight:500">{{ assigneeName(amlCase.assignedTo) }}</div>
                    <div style="font-size:11px;color:#888">Case officer</div>
                  </div>
                </div>
                <div v-else class="aml-muted" style="font-size:12px">Unassigned</div>
              </div>
            </div>

          </v-col>
        </v-row>
      </div>

      <!-- ══ TAB 1 — Stages & actions ══ -->
      <div v-show="tab === 1" class="aml-tab-content">
        <div v-if="!groupedStages.length" class="aml-empty-panel pa-8 text-center">
          <v-icon size="40" color="grey lighten-2">mdi-clipboard-list-outline</v-icon>
          <div class="mt-2" style="color:#bbb;font-size:13px">No monitoring cycle actions seeded for this case.</div>
        </div>
        <div v-for="(stage, si) in groupedStages" :key="stage.name" class="aml-stage-card mb-4" :class="{ 'aml-stage-active': stage.name === amlCase.reportStage, 'aml-stage-blocked': si > currentStageIndex }">
          <div class="aml-stage-head">
            <div class="aml-stage-num" :style="stageNumStyle(stage, si)">{{ si + 1 }}</div>
            <div class="ml-3 flex-grow-1">
              <div style="font-size:13px;font-weight:500">{{ stage.name }}</div>
              <div style="font-size:11px;color:#999">{{ stage.actions.length }} action{{ stage.actions.length !== 1 ? 's' : '' }} · {{ stage.completedCount }}/{{ stage.realCount }} done</div>
            </div>
            <span class="aml-pill" :style="stagePillStyle(stage, si)">{{ stageLabel(stage, si) }}</span>
          </div>

          <!-- Actions -->
          <div v-if="!stage.isPlaceholderOnly">
            <div
              v-for="action in stage.actions"
              :key="action.id"
              class="aml-action-row"
              :class="{ 'aml-action-done': action.complete, 'aml-action-waived': isWaived(action) }"
            >
              <div class="mr-3 mt-1" style="flex-shrink:0">
                <v-icon small :color="actionIconColor(action)">{{ actionIcon(action) }}</v-icon>
              </div>
              <div class="flex-grow-1 min-width-0">
                <div style="font-size:13px;font-weight:500">{{ action.action }}</div>
                <div v-if="actionDescription(action)" class="aml-muted" style="font-size:11px;margin-top:2px">{{ actionDescription(action) }}</div>
                <div v-if="isWaived(action)" class="aml-muted" style="font-size:11px;color:#D85A30;margin-top:2px">Waived: {{ waivedReason(action) }}</div>
                <div v-if="action.complete" class="aml-muted" style="font-size:11px;color:#3B6D11;margin-top:2px">Completed by {{ assigneeName(action.user) }}</div>
              </div>
              <div v-if="!isClosed && si === currentStageIndex" class="d-flex align-center ml-2" style="gap:6px;flex-shrink:0">
                <template v-if="!action.complete && !isWaived(action)">
                  <button class="aml-action-btn aml-action-btn--primary" @click="markComplete(action)">Mark done</button>
                  <button v-if="!actionMeta(action).isRequired" class="aml-action-btn aml-action-btn--warn" @click="openWaiveDialog(action)">Waive</button>
                </template>
                <span v-else-if="action.complete" class="aml-muted" style="font-size:11px">✓ Done</span>
                <span v-else-if="isWaived(action)" class="aml-muted" style="font-size:11px;color:#D85A30">Waived</span>
              </div>
            </div>
          </div>
          <div v-else class="aml-action-row">
            <div class="aml-muted" style="font-size:12px">No actions configured for this stage.</div>
          </div>
        </div>

        <!-- All stages complete banner -->
        <v-alert
          v-if="overallProgress.allDone && !isClosed"
          type="success"
          outlined
          class="mt-4"
          icon="mdi-check-circle-outline"
        >
          <div class="d-flex align-center justify-space-between flex-wrap" style="gap:8px">
            <div>
              <div class="body-2 font-weight-medium">All stages complete</div>
              <div class="caption">Every required action has been completed or waived. You can now close this investigation.</div>
            </div>
            <button
              class="aml-hdr-btn"
              style="background:#EAF3DE;color:#3B6D11;border-color:#3B6D11"
              @click="closePromptDialog = true; closePromptDismissed = false"
            >
              <v-icon x-small left>mdi-lock-check-outline</v-icon>Close investigation
            </button>
          </div>
        </v-alert>

        <v-alert
          v-else-if="!overallProgress.allDone && !isClosed && overallProgress.total > 0"
          type="info" outlined dense class="mt-4"
          icon="mdi-information-outline"
        >
          <span class="caption">{{ overallProgress.total - overallProgress.done }} action{{ (overallProgress.total - overallProgress.done) !== 1 ? 's' : '' }} still pending.</span>
        </v-alert>
      </div>

      <!-- ══ TAB 2 — Search history ══ -->
      <div v-show="tab === 2" class="aml-tab-content">
        <div class="aml-panel">
          <div class="aml-panel-head">
            <div class="aml-panel-icon" style="background:#E6F1FB">
              <v-icon small color="#185FA5">mdi-history</v-icon>
            </div>
            <span class="aml-panel-title">Search history for this account</span>
          </div>
          <div v-if="!searchLogs.length" class="aml-empty-panel">No search logs found.</div>
          <table v-else class="aml-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Provider</th>
                <th>Trigger</th>
                <th>Status</th>
                <th>Score</th>
                <th>By</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="log in searchLogs"
                :key="log.id"
                class="aml-table-row"
                @click="expandedLog = expandedLog === log.id ? null : log.id"
              >
                <td class="aml-muted">{{ formatDate(log.dateCreated, 'YYYY-MM-DD HH:mm') }}</td>
                <td><span class="aml-type-badge">{{ providerShort(log.provider) }}</span></td>
                <td><span class="aml-type-badge">{{ log.triggerType }}</span></td>
                <td><span class="aml-pill" :style="matchStatusStyle(log.matchStatus)">{{ log.matchStatus }}</span></td>
                <td class="aml-muted">{{ log.matchScore || '—' }}</td>
                <td class="aml-muted">{{ log.triggeredBy ? assigneeName(log.triggeredBy) : 'System' }}</td>
              </tr>
              <template v-for="log in searchLogs">
                <tr v-if="expandedLog === log.id" :key="'exp-' + log.id">
                  <td colspan="6" class="pa-3" style="background:#fafafa">
                    <div v-if="!log.matchDetails || !log.matchDetails.length" class="aml-muted" style="font-size:12px">No match details.</div>
                    <div v-for="(hit, i) in log.matchDetails" :key="i" class="aml-hit-card mb-2">
                      <div class="d-flex align-center justify-space-between mb-1">
                        <span style="font-size:12px;font-weight:600">{{ hit.name }}</span>
                        <span class="aml-pill" style="background:#FCEBEB;color:#A32D2D">Score: {{ hit.score }}</span>
                      </div>
                      <div class="d-flex" style="gap:4px;flex-wrap:wrap">
                        <span class="aml-type-badge">{{ hit.source_code }}</span>
                        <span class="aml-type-badge">{{ hit.type }}</span>
                        <span class="aml-type-badge">{{ hit.data_set }}</span>
                      </div>
                    </div>
                  </td>
                </tr>
              </template>
            </tbody>
          </table>
        </div>
      </div>

      <!-- ══ TAB 3 — Comments ══ -->
      <div v-show="tab === 3" class="aml-tab-content">
        <div v-if="!amlCase.comments || !amlCase.comments.length" class="aml-empty-panel text-center pa-8">
          <v-icon size="40" color="grey lighten-2">mdi-comment-outline</v-icon>
          <div class="mt-2" style="color:#bbb;font-size:13px">No comments yet.</div>
          <v-btn small depressed color="primary" class="mt-3" @click="commentDialog = true">Add first note</v-btn>
        </div>
        <div v-for="comment in amlCase.comments" :key="comment.id" class="aml-comment-card mb-3">
          <div class="aml-comment-header">
            <div class="aml-comment-avatar">{{ avatarInitials(assigneeName(comment.user)) }}</div>
            <div class="ml-2 flex-grow-1">
              <div style="font-size:12px;font-weight:500">{{ assigneeName(comment.user) }}</div>
              <div style="font-size:11px;color:#999">{{ formatDate(comment.dateCreated, 'YYYY-MM-DD HH:mm') }}</div>
            </div>
            <span class="aml-type-badge">{{ comment.type }}</span>
            <template v-if="comment.userId === (loggedInUser && loggedInUser.id)">
              <v-btn icon x-small class="ml-1" @click="editComment(comment)"><v-icon x-small>mdi-pencil-outline</v-icon></v-btn>
              <v-btn icon x-small @click="confirmDeleteComment(comment)"><v-icon x-small color="error">mdi-delete-outline</v-icon></v-btn>
            </template>
          </div>
          <div class="aml-comment-body">{{ comment.message }}</div>
          <div v-if="comment.evidenceUrls && comment.evidenceUrls.length" class="px-3 pb-2 d-flex flex-wrap" style="gap:6px">
            <a v-for="url in comment.evidenceUrls" :key="url" :href="url" target="_blank" class="aml-evidence-link">
              <v-icon x-small left>mdi-link</v-icon>Evidence
            </a>
          </div>
        </div>
      </div>

    </div>

    <!-- ══ Comment Dialog ══ -->
    <AmlCommentDialog
      v-model="commentDialog"
      :editing-comment="editingComment"
      :saving="commentSaving"
      @submit="submitComment"
      @close="closeCommentDialog"
    />

    <!-- ══ Close Prompt — all stages done ══ -->
    <v-dialog v-model="closePromptDialog" max-width="440" persistent>
      <v-card>
        <v-card-title class="d-flex align-center">
          <v-icon left color="success">mdi-lock-check-outline</v-icon>
          <span class="subtitle-1 font-weight-medium">Close this investigation?</span>
        </v-card-title>
        <v-divider />
        <v-card-text class="pt-4 body-2">
          All stages and actions for <strong>{{ amlCase && amlCase.caseReference }}</strong> have been completed. Would you like to proceed with closing the investigation?
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="closePromptDialog = false; closePromptDismissed = true">Not yet</v-btn>
          <v-btn depressed color="success" @click="openCloseDialog">
            <v-icon left small>mdi-check</v-icon>Yes, close it
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ══ Close Case Dialog ══ -->
    <v-dialog v-model="closeDialog" max-width="560" persistent>
      <v-card>
        <v-card-title class="d-flex align-center">
          <v-icon left color="#A32D2D">mdi-lock-outline</v-icon>
          <span class="subtitle-1 font-weight-medium">Close watchlist case</span>
          <v-spacer /><v-btn icon @click="closeDialog = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-divider />
        <v-card-text class="pt-4">
          <v-form ref="closeForm">
            <v-select
              v-model="closeForm.status"
              :items="closeStatusOptions"
              item-text="text" item-value="value"
              label="Close outcome *"
              dense outlined
              prepend-inner-icon="mdi-list-status"
              class="mb-3"
              :rules="[v => !!v || 'Required']"
            />
            <!-- Flag determination -->
            <v-select
              v-model="closeForm.flagOutcome"
              :items="flagOutcomeOptions"
              item-text="text" item-value="value"
              label="Flag determination (applies to all linked flags) *"
              dense outlined
              prepend-inner-icon="mdi-alert-circle-outline"
              class="mb-3"
              hint="This will update the watchlist flag status"
              persistent-hint
              :rules="[v => !!v || 'Required']"
            />
            <v-text-field v-model="closeForm.closeReason" label="Close reason *" dense outlined prepend-inner-icon="mdi-text-box-outline" class="mb-3" :rules="[v => !!v || 'Required']" />
            <v-textarea v-model="closeForm.flagReviewNote" label="Flag review note (optional)" rows="2" dense outlined />
          </v-form>
        </v-card-text>
        <v-divider />
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="closeDialog = false">Cancel</v-btn>
          <v-btn depressed color="error" :loading="closing" @click="submitClose">
            <v-icon left small>mdi-lock-outline</v-icon>Close case
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ══ Assign Dialog ══ -->
    <v-dialog v-model="assignDialog" max-width="420" persistent>
      <v-card>
        <v-card-title class="subtitle-1 font-weight-medium">
          Assign case <v-spacer /><v-btn icon @click="assignDialog = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-divider />
        <v-card-text class="pt-4">
          <v-autocomplete v-model="assignUserId" :items="users" :loading="loadingUsers" item-text="displayName" item-value="id" label="Assign to *" dense outlined no-data-text="No users found" />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="assignDialog = false">Cancel</v-btn>
          <v-btn color="primary" depressed :loading="assigning" :disabled="!assignUserId" @click="submitAssign">Assign</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ══ Waive Action Dialog ══ -->
    <v-dialog v-model="waiveDialog" max-width="420" persistent>
      <v-card>
        <v-card-title class="d-flex align-center">
          <v-icon left color="orange darken-2">mdi-close-circle-outline</v-icon>
          <span class="subtitle-1 font-weight-medium">Waive action</span>
          <v-spacer /><v-btn icon @click="waiveDialog = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-divider />
        <v-card-text class="pt-4">
          <div class="body-2 mb-3">Provide a reason for waiving <strong>{{ waiveTarget && waiveTarget.action }}</strong>.</div>
          <v-textarea v-model="waiveReason" label="Reason *" rows="3" dense outlined />
        </v-card-text>
        <v-divider />
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="waiveDialog = false">Cancel</v-btn>
          <v-btn depressed color="orange darken-2" dark :disabled="!waiveReason.trim()" :loading="waiving" @click="submitWaive">Waive</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

  </div>
</template>

<script>
import { mapState }          from 'vuex';
import PageMixin             from '@/mixins/page.mixin';
import FormatMixin           from '@/mixins/format.mixin';
import AmlMixin              from '@/mixins/aml.mixin';
import { WatchlistService }   from '@/modules/amlpro/services/watchlist.service';
import { UserService }        from '@/modules/global-settings/security/services/user.service';
import AmlCommentDialog      from '../aml-cases/AmlCommentDialog.vue';
import {
  providerShort, describeListDate, isSafeUrl,
} from '@/modules/amlpro/services/watchlist-sources';

const svc     = new WatchlistService();
const userSvc = new UserService();

export default {
  name: 'WatchlistCaseDetail',
  mixins: [PageMixin, FormatMixin, AmlMixin],
  components: { AmlCommentDialog },
  props: { caseId: { type: String, required: true } },

  data() {
    return {
      loading:    true,
      amlCase:    null,
      searchLogs: [],
      tab:        0,
      expandedLog: null,

      // Comments
      commentDialog:  false,
      commentSaving:  false,
      editingComment: null,
      deletingComment: false,

      // Close
      closeDialog: false,
      closing:     false,
      closeForm:   { status: '', flagOutcome: null, closeReason: '', flagReviewNote: '' },

      // Assign
      assignDialog: false,
      assigning:    false,
      assignUserId: null,
      users:        [],
      loadingUsers: false,

      // Waive
      waiveDialog: false,
      waiving:     false,
      waiveTarget: null,
      waiveReason: '',

      // Close prompt
      closePromptDialog:    false,
      closePromptDismissed: false,

      // Expanded hit cards in flag panel
      expandedCaseHits: {},

      closeStatusOptions: [
        { text: 'Investigation completed', value: 'CLOSED_INVESTIGATION_COMPLETED' },
        { text: 'No action taken',         value: 'CLOSED_NO_ACTION' },
        { text: 'Report filed',            value: 'CLOSED_REPORT_FILED' },
      ],
      flagOutcomeOptions: [
        { text: 'True positive — confirmed hit', value: 'TRUE_POSITIVE' },
        { text: 'False positive — cleared',      value: 'FALSE_POSITIVE' },
      ],
    };
  },

  computed: {
    ...mapState('authModule', { loggedInUser: 'user' }),

    isClosed() {
      return ['CLOSED_INVESTIGATION_COMPLETED','CLOSED_NO_ACTION','CLOSED_REPORT_FILED'].includes(this.amlCase?.status);
    },

    tabDefs() {
      const actions  = this.amlCase?.actions ?? [];
      const real     = actions.filter(a => !a.comment?.includes('empty=true'));
      const done     = real.filter(a => a.complete || a.comment?.includes('WAIVED:'));
      const comments = this.amlCase?.comments ?? [];
      return [
        { label: 'Overview' },
        { label: 'Investigation stages', badge: `${done.length}/${real.length}`, badgeDone: done.length === real.length && real.length > 0 },
        { label: 'Search history',       badge: this.searchLogs.length },
        { label: 'Notes',                badge: comments.length },
      ];
    },

    latestSearchLog() {
      return this.amlCase?.watchlistFlag?.searchLogs?.[0] ?? null;
    },

    allLinkedFlags() {
      if (!this.amlCase) return [];
      const seen = new Set();
      const flags = [];

      if (this.amlCase.watchlistFlags && this.amlCase.watchlistFlags.length) {
        for (const link of this.amlCase.watchlistFlags) {
          const f = link.flag;
          if (!f || seen.has(f.id)) continue;
          seen.add(f.id);
          flags.push({
            flagId:             f.id,
            provider:           f.provider,
            listName:           f.listName,
            flagStatus:         f.flagStatus,
            lastReviewedStatus: f.lastReviewedStatus ?? null,
            isPrimary:          link.isPrimary,
            latestLog:          f.searchLogs?.[0] ?? null,
          });
        }
      }

      if (flags.length === 0 && this.amlCase.watchlistFlag) {
        const f = this.amlCase.watchlistFlag;
        flags.push({
          flagId:             f.id,
          provider:           f.provider,
          listName:           f.listName,
          flagStatus:         f.flagStatus,
          lastReviewedStatus: f.lastReviewedStatus ?? null,
          isPrimary:          true,
          latestLog:          f.searchLogs?.[0] ?? null,
        });
      }

      return flags;
    },

    groupedStages() {
      const actions = this.amlCase?.actions ?? [];
      const map     = new Map();

      for (const a of actions) {
        const stageName  = a.stage ?? 'Unknown';
        const stageOrder = this.parseStageOrder(a.comment);
        if (!map.has(stageName)) {
          map.set(stageName, { name: stageName, order: stageOrder, actions: [] });
        }
        map.get(stageName).actions.push(a);
      }

      return [...map.values()]
        .sort((a, b) => a.order - b.order)
        .map(stage => {
          const real = stage.actions.filter(a => !a.comment?.includes('empty=true'));
          return {
            ...stage,
            isPlaceholderOnly: real.length === 0,
            realCount:         real.length,
            completedCount:    real.filter(a => a.complete || a.comment?.includes('WAIVED:')).length,
          };
        });
    },

    currentStageIndex() {
      return this.groupedStages.findIndex(s => s.name === this.amlCase?.reportStage);
    },

    overallProgress() {
      const all  = this.groupedStages.flatMap(s => s.actions.filter(a => !a.comment?.includes('empty=true')));
      const done = all.filter(a => a.complete || a.comment?.includes('WAIVED:')).length;
      return { done, total: all.length, allDone: all.length > 0 && done === all.length };
    },

    // Extract the primary person under investigation from account.person
    subjectPerson() {
      const persons = this.amlCase?.account?.person ?? [];
      if (!persons.length) return null;
      const main = persons.find(p => p.personClass === 'MEMBER') ?? persons[0];
      return main?.person ?? null;
    },

    primaryContact() {
      const contacts = this.subjectPerson?.contact ?? [];
      if (!contacts.length) return null;
      return contacts.find(c => c.isPrimary || c.primary) ?? contacts[0] ?? null;
    },

    subjectAddress() {
      const addr = this.subjectPerson?.livingAddress;
      if (!addr) return null;
      const parts = [addr.street, addr.city, addr.province, addr.country].filter(Boolean);
      return parts.join(', ') || null;
    },
  },

  mounted() {
    this.loadCase();
    this.loadUsers();
  },

  methods: {
    providerShort, describeListDate, isSafeUrl,

    async loadCase() {
      this.loading = true;
      try {
        const res     = await svc.fetchCase(this.caseId);
        this.amlCase  = res.data ?? res;
        if (this.amlCase?.accountId) {
          this.loadSearchLogs(this.amlCase.accountId);
        }
      } catch {
        this.showSnack({ color: 'error', title: 'Error', message: 'Failed to load case' });
      } finally {
        this.loading = false;
      }
    },

    async loadSearchLogs(accountId) {
      try {
        const res       = await svc.fetchSearchLogsByAccount(accountId, { limit: 50 });
        const data      = res.data ?? res;
        this.searchLogs = data.data ?? data;
      } catch { this.searchLogs = []; }
    },

    async acknowledgeActivity() {
      try {
        await svc.acknowledgeSearch(this.caseId);
        this.amlCase.hasNewSearchActivity = false;
      } catch { /* silent */ }
    },

    editComment(comment) { this.editingComment = comment; this.commentDialog = true; },
    closeCommentDialog() { this.commentDialog = false; this.editingComment = null; },

    async submitComment(payload) {
      this.commentSaving = true;
      try {
        if (this.editingComment) {
          await svc.updateComment(this.caseId, this.editingComment.id, payload.message);
          this.showSnack({ color: 'success', title: 'Updated', message: 'Comment updated' });
        } else {
          await svc.addComment(this.caseId, payload);
          this.showSnack({ color: 'success', title: 'Added', message: 'Note added' });
        }
        this.closeCommentDialog();
        this.loadCase();
      } catch {
        this.showSnack({ color: 'error', title: 'Error', message: 'Failed to save comment' });
      } finally {
        this.commentSaving = false;
      }
    },

    async confirmDeleteComment(comment) {
      if (!confirm('Delete this comment?')) return;
      try {
        await svc.deleteComment(this.caseId, comment.id);
        this.showSnack({ color: 'success', title: 'Deleted', message: 'Comment deleted' });
        this.loadCase();
      } catch {
        this.showSnack({ color: 'error', title: 'Error', message: 'Failed to delete comment' });
      }
    },

    async submitClose() {
      if (!this.$refs.closeForm.validate()) return;
      this.closing = true;
      try {
        await svc.closeCase(this.caseId, this.closeForm);
        this.showSnack({ color: 'success', title: 'Closed', message: `Case ${this.amlCase.caseReference} closed` });
        this.closeDialog       = false;
        this.closePromptDialog = false;
        setTimeout(() => this.$router.push({ name: 'watchlist-dashboard' }), 1200);
      } catch (e) {
        const msg = e?.response?.data?.message ?? 'Failed to close case';
        this.showSnack({ color: 'error', title: 'Error', message: Array.isArray(msg) ? msg.join(', ') : msg });
      } finally {
        this.closing = false;
      }
    },

    async openAssignDialog() {
      this.assignDialog = true;
      // Default to current assignee, fallback to logged-in user
      this.assignUserId = this.amlCase.assignedToUserId
        ?? (this.loggedInUser?.id ?? null);
      if (!this.users.length) await this.loadUsers();
    },

    async loadUsers() {
      this.loadingUsers = true;
      try {
        const res  = await userSvc.findAll();
        const data = res.data ?? res;
        const list = data.users ?? data;
        this.users = list.map(u => ({
          id: u.id,
          displayName: u.person ? `${u.person.firstName ?? ''} ${u.person.lastName ?? ''}`.trim() || u.username : u.username,
        }));
      } catch { /* silent */ } finally { this.loadingUsers = false; }
    },

    async submitAssign() {
      this.assigning = true;
      try {
        await svc.assignCase(this.caseId, this.assignUserId);
        this.showSnack({ color: 'success', title: 'Assigned', message: 'Case assigned' });
        this.assignDialog = false;
        this.loadCase();
      } catch {
        this.showSnack({ color: 'error', title: 'Error', message: 'Failed to assign case' });
      } finally { this.assigning = false; }
    },

    async assignToSelf() {
      try {
        await svc.assignCase(this.caseId, this.loggedInUser.id);
        this.showSnack({ color: 'success', title: 'Assigned', message: 'Case assigned to you' });
        this.loadCase();
      } catch {
        this.showSnack({ color: 'error', title: 'Error', message: 'Failed to assign' });
      }
    },

    async markComplete(action) {
      try {
        await svc.updateAction(this.caseId, action.id, { complete: true });
        await this.loadCase();
        this.checkForCompletion();
      } catch {
        this.showSnack({ color: 'error', title: 'Error', message: 'Failed to mark complete' });
      }
    },

    openWaiveDialog(action) { this.waiveTarget = action; this.waiveReason = ''; this.waiveDialog = true; },

    async submitWaive() {
      if (!this.waiveReason.trim()) return;
      this.waiving = true;
      try {
        await svc.updateAction(this.caseId, this.waiveTarget.id, {
          complete: false,
          comment:  `WAIVED: ${this.waiveReason}`,
        });
        this.waiveDialog = false;
        await this.loadCase();
        this.checkForCompletion();
      } catch {
        this.showSnack({ color: 'error', title: 'Error', message: 'Failed to waive action' });
      } finally { this.waiving = false; }
    },

    checkForCompletion() {
      if (this.overallProgress.allDone && !this.isClosed && !this.closePromptDismissed) {
        this.$nextTick(() => { this.closePromptDialog = true; });
      }
    },

    openCloseDialog() { this.closePromptDialog = false; this.closeDialog = true; },

    goToFlag(flagId) { this.$router.push({ name: 'watchlist-flag-detail', params: { flagId } }); },

    parseStageOrder(comment) {
      if (!comment) return 999;
      const m = comment.match(/^__meta:[^_]*stageOrder=(\d+)/);
      return m ? parseInt(m[1], 10) : 999;
    },

    stageLabel(stage, si) {
      if (si < this.currentStageIndex) return 'Complete';
      if (si === this.currentStageIndex) return 'Active';
      return 'Upcoming';
    },

    stagePillStyle(stage, si) {
      if (si < this.currentStageIndex)  return 'background:#EAF3DE;color:#3B6D11';
      if (si === this.currentStageIndex) return 'background:#E6F1FB;color:#185FA5';
      return 'background:#f5f5f5;color:#aaa';
    },

    stageNumStyle(stage, si) {
      if (si < this.currentStageIndex)  return 'background:#EAF3DE;color:#3B6D11';
      if (si === this.currentStageIndex) return 'background:#185FA5;color:white';
      return 'background:#f5f5f5;color:#aaa';
    },

    actionMeta(action) {
      const m = action.comment?.match(/^__meta:[^_]*isRequired=(true|false)/);
      return { isRequired: m ? m[1] === 'true' : true };
    },

    actionDescription(action) {
      if (!action.comment) return '';
      const stripped = action.comment.replace(/^__meta:[^_]+__/, '');
      if (stripped.startsWith('WAIVED:')) return '';
      return stripped;
    },

    isWaived(action)   { return !!action.comment?.includes('WAIVED:'); },
    waivedReason(action) { return action.comment?.replace(/^WAIVED:\s*/, '') ?? ''; },

    actionIcon(action) {
      if (action.complete)  return 'mdi-check-circle';
      if (this.isWaived(action)) return 'mdi-close-circle-outline';
      return 'mdi-circle-outline';
    },

    actionIconColor(action) {
      if (action.complete)  return '#3B6D11';
      if (this.isWaived(action)) return '#D85A30';
      return '#ccc';
    },

    flagStatusLabel(s) {
      const map = { POSSIBLE_MATCH_UNREVIEWED: 'Unreviewed', UNDER_REVIEW: 'Under review', TRUE_POSITIVE: 'Confirmed hit', FALSE_POSITIVE: 'Cleared' };
      return map[s] ?? s;
    },

    flagStatusStyle(s) {
      const map = { POSSIBLE_MATCH_UNREVIEWED: 'background:#FCEBEB;color:#A32D2D', UNDER_REVIEW: 'background:#E6F1FB;color:#185FA5', TRUE_POSITIVE: 'background:#FCEBEB;color:#A32D2D', FALSE_POSITIVE: 'background:#EAF3DE;color:#3B6D11' };
      return map[s] ?? 'background:#f5f5f5;color:#888';
    },

    caseStatusStyle(s) {
      const map = { OPEN: 'background:#E6F1FB;color:#185FA5', REOPENED: 'background:#FFF4E5;color:#854F0B', CLOSED_INVESTIGATION_COMPLETED: 'background:#EAF3DE;color:#3B6D11', CLOSED_NO_ACTION: 'background:#F1EFE8;color:#5F5E5A', CLOSED_REPORT_FILED: 'background:#EEEDFE;color:#534AB7' };
      return map[s] ?? 'background:#f5f5f5;color:#888';
    },

    priorityPillStyle(p) {
      const map = { HIGH: 'background:#FCEBEB;color:#A32D2D', MEDIUM: 'background:#FAEEDA;color:#854F0B', LOW: 'background:#EAF3DE;color:#3B6D11' };
      return map[p] ?? 'background:#f5f5f5;color:#888';
    },

    matchStatusStyle(s) {
      const map = { POSSIBLE_MATCH: 'background:#FCEBEB;color:#A32D2D', NO_MATCH: 'background:#EAF3DE;color:#3B6D11' };
      return map[s] ?? 'background:#f5f5f5;color:#888';
    },

    avatarInitials(name) {
      if (!name || name === '—') return '?';
      return name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
    },
  },
};
</script>

<style scoped>
.aml-detail-root  { font-family:inherit; }
.aml-detail-wrap  { padding:20px 24px 32px; }
.aml-detail-header { display:flex; align-items:center; margin-bottom:20px; flex-wrap:wrap; gap:8px; }
.aml-back-btn     { width:34px; height:34px; border-radius:8px; border:0.5px solid #e0e0e0; background:transparent; cursor:pointer; display:flex; align-items:center; justify-content:center; transition:background 0.12s; flex-shrink:0; }
.aml-back-btn:hover { background:#f5f5f5; }
.aml-case-ref     { font-size:20px; font-weight:500; letter-spacing:-0.3px; }
.aml-case-sub     { font-size:12px; color:#999; margin-top:2px; }
.aml-header-divider { width:1px; height:24px; background:#e0e0e0; margin:0 4px; align-self:center; }
.aml-hdr-btn      { font-size:12px; padding:5px 12px; border-radius:8px; border:0.5px solid #ddd; background:transparent; color:inherit; cursor:pointer; display:inline-flex; align-items:center; gap:4px; transition:background 0.12s; font-family:inherit; }
.aml-hdr-btn:hover { background:#f5f5f5; }
.aml-hdr-btn--danger { background:#FCEBEB; color:#A32D2D; border-color:#F09595; }
.aml-hdr-btn--danger:hover { background:#F7C1C1; }
.aml-status-pill  { display:inline-flex; align-items:center; gap:3px; padding:3px 10px; border-radius:20px; font-size:11px; font-weight:600; }
.aml-tabs-bar     { display:flex; border-bottom:0.5px solid #e0e0e0; margin-bottom:20px; gap:0; overflow-x:auto; }
.aml-tab          { font-size:13px; padding:10px 18px; border:none; background:transparent; color:#888; cursor:pointer; border-bottom:2px solid transparent; transition:all 0.15s; white-space:nowrap; display:inline-flex; align-items:center; gap:6px; font-family:inherit; }
.aml-tab--active  { color:#185FA5; border-bottom-color:#185FA5; font-weight:500; }
.aml-tab-badge    { font-size:10px; font-weight:700; padding:1px 6px; border-radius:10px; background:#F1EFE8; color:#888; }
.aml-tab-badge--done { background:#EAF3DE; color:#3B6D11; }
.aml-panel        { border:0.5px solid #e0e0e0; border-radius:12px; overflow:hidden; background:white; }
.aml-panel-head   { display:flex; align-items:center; padding:12px 14px; border-bottom:0.5px solid #e0e0e0; gap:8px; }
.aml-panel-icon   { width:28px; height:28px; border-radius:8px; display:flex; align-items:center; justify-content:center; flex-shrink:0; }
.aml-panel-title  { font-size:13px; font-weight:500; }
.aml-grid-2       { display:grid; grid-template-columns:1fr 1fr; gap:10px 16px; }
.aml-field        {}
.aml-label        { font-size:11px; color:#999; margin-bottom:2px; }
.aml-value        { font-size:12px; }
.aml-section-label { font-size:10px; font-weight:600; letter-spacing:.5px; text-transform:uppercase; color:#aaa; }
.aml-divider      { height:0.5px; background:#e0e0e0; }
.aml-muted        { color:#777; }
.aml-pill         { display:inline-block; padding:2px 8px; border-radius:20px; font-size:10px; font-weight:500; white-space:nowrap; }
.aml-type-badge   { font-size:10px; font-weight:700; color:#666; background:#f5f5f5; padding:2px 6px; border-radius:4px; }
.aml-ref-link     { color:#185FA5; font-weight:600; }
.aml-empty-panel  { color:#bbb; font-size:12px; }
.aml-table        { width:100%; border-collapse:collapse; font-size:12px; }
.aml-table thead tr { border-bottom:0.5px solid #e0e0e0; }
.aml-table th     { padding:9px 14px; text-align:left; font-size:10px; font-weight:600; color:#aaa; letter-spacing:.5px; text-transform:uppercase; }
.aml-table-row    { border-bottom:0.5px solid #e0e0e0; cursor:pointer; transition:background 0.1s; }
.aml-table-row:hover { background:#EBF3FC; }
.aml-table td     { padding:10px 14px; }
.aml-hit-card     { border:0.5px solid #e0e0e0; border-radius:8px; padding:10px 12px; background:#fafafa; }
.aml-comment-card     { border:0.5px solid #e0e0e0; border-radius:10px; overflow:hidden; }
.aml-comment-header   { display:flex; align-items:center; padding:10px 14px; background:#fafafa; border-bottom:0.5px solid #e0e0e0; }
.aml-comment-avatar   { width:28px; height:28px; border-radius:50%; background:#E6F1FB; color:#185FA5; display:flex; align-items:center; justify-content:center; font-size:10px; font-weight:700; font-family:monospace; flex-shrink:0; }
.aml-comment-body     { padding:12px 14px; font-size:13px; white-space:pre-wrap; line-height:1.6; }
.aml-evidence-link    { font-size:11px; color:#185FA5; text-decoration:none; display:inline-flex; align-items:center; }
.aml-source-link      { font-size:10px; color:#185FA5; text-decoration:none; display:inline-flex; align-items:center; gap:2px; }
.aml-source-link:hover { text-decoration:underline; }
.aml-stage-card       { border:0.5px solid #e0e0e0; border-radius:12px; overflow:hidden; background:white; }
.aml-stage-card.aml-stage-blocked { background:#fafafa; }
.aml-stage-card.aml-stage-active  { border-color:#B5D4F4; background:#f0f7ff; }
.aml-stage-head       { display:flex; align-items:center; padding:12px 16px; }
.aml-stage-num        { width:24px; height:24px; border-radius:50%; display:flex; align-items:center; justify-content:center; font-size:11px; font-weight:700; flex-shrink:0; }
.aml-action-row       { display:flex; align-items:flex-start; padding:12px 16px; border-bottom:0.5px solid #f0f0f0; }
.aml-action-row:last-child { border-bottom:none; }
.aml-action-done      { background:#f8fdf5; }
.aml-action-waived    { background:#fffbf5; }
.aml-action-btn       { font-size:11px; padding:3px 10px; border-radius:6px; border:0.5px solid #ddd; background:transparent; cursor:pointer; font-family:inherit; transition:all 0.1s; }
.aml-action-btn--primary { background:#185FA5; color:white; border-color:#185FA5; }
.aml-action-btn--primary:hover { background:#0C447C; }
.aml-action-btn--warn { background:#FAEEDA; color:#854F0B; border-color:#FAC775; }
.aml-action-btn--warn:hover { background:#FAC775; }
.aml-person-avatar    { width:36px; height:36px; border-radius:50%; background:#185FA5; color:white; display:flex; align-items:center; justify-content:center; font-size:11px; font-weight:700; font-family:monospace; flex-shrink:0; }
.min-width-0          { min-width:0; }
.aml-flag-link-row            { padding:12px 14px; }
.aml-flag-link-row--bordered  { border-bottom:0.5px solid #f0f0f0; }
</style>